import { api } from '../services/api';
import { logger } from './logger';

/**
 * 🛠️ УТИЛИТЫ для работы с фильмами
 * 
 * ❓ ПОЧЕМУ ЭТО UTILS (а не хуки)?
 * - Это ЧИСТЫЕ ФУНКЦИИ без состояния
 * - Можно вызывать где угодно (компоненты, хуки, другие функции)
 * - Не привязаны к React lifecycle
 * - Просто получают данные и возвращают результат
 */

/**
 * 📡 Получить детальную информацию о фильме из TMDB
 * 
 * 🔄 Как работает:
 * 1. Делает запрос к нашему серверу /movies/details/123
 * 2. Сервер обращается к TMDB API за полной информацией
 * 3. Возвращает объект с деталями фильма (постер, описание, жанры, etc.)
 * 
 * 💡 Используется когда:
 * - Пользователь выбрал фильм из поиска
 * - Переход по ссылке с tmdbId в URL
 * - Нужны полные данные фильма для отображения
 * 
 * @param tmdbId - ID фильма в базе TMDB (например: 550 для Fight Club)
 * @returns Promise с объектом данных фильма
 */
export const fetchMovieDetails = async (tmdbId: string | number) => {
  try {
    const response = await api.get(`/movies/details/${tmdbId}`);
    logger.movieLoaded(response.data.title);
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch movie details', error);
    throw error;
  }
};

/**
 * 📋 Проверить, есть ли фильм в списке "хочу посмотреть"
 * 
 * 🔄 Как работает:
 * 1. Читает массив ID из localStorage['rewatch-watchlist']
 * 2. Проверяет, есть ли переданный tmdbId в этом массиве
 * 3. Возвращает true/false
 * 
 * 💾 Структура в localStorage:
 * 'rewatch-watchlist': '["550", "13", "680"]' (массив строк с ID фильмов)
 * 
 * 💡 Используется для:
 * - Показа бейджа "Added to watchlist" 
 * - Условного рендеринга кнопок
 * 
 * @param tmdbId - ID фильма в TMDB
 * @returns true если фильм в watchlist, false если нет
 */
export const isMovieInWatchlist = (tmdbId: string | number): boolean => {
  // Получаем массив ID из localStorage, если нет - пустой массив
  const watchlist = JSON.parse(localStorage.getItem('rewatch-watchlist') || '[]');
  
  // Проверяем наличие ID (приводим к строке для сравнения)
  const isInList = watchlist.includes(tmdbId.toString());
  
  console.log(`📋 Checking watchlist for ${tmdbId}:`, isInList);
  return isInList;
};

/**
 * ➕ Добавить фильм в список "хочу посмотреть"
 * 
 * 🔄 Как работает:
 * 1. Читает текущий watchlist из localStorage
 * 2. Проверяет, нет ли фильма уже в списке (избегаем дубликатов)
 * 3. Добавляет новый ID в массив
 * 4. Сохраняет обновленный массив обратно в localStorage
 * 
 * 💡 Используется когда:
 * - Пользователь нажимает "Add to Watchlist"
 * - Автоматическое добавление при переходе к добавлению фильма
 * 
 * @param tmdbId - ID фильма для добавления
 */
/**
 * ➕ Добавить фильм в список "хочу посмотреть"
 * 
 * 🔄 Как работает:
 * 1. Читает текущий watchlist из localStorage
 * 2. Проверяет, нет ли уже этого фильма
 * 3. Добавляет в массив если его нет
 * 4. Сохраняет обновленный массив
 * 
 * @param tmdbId - ID фильма для добавления
 */
export const addToWatchlist = (tmdbId: string | number): void => {
  // Получаем текущий список
  const watchlist = JSON.parse(localStorage.getItem('rewatch-watchlist') || '[]');
  
  // Добавляем только если еще нет в списке
      if (!watchlist.includes(tmdbId.toString())) {
        watchlist.push(tmdbId.toString());
        localStorage.setItem('rewatch-watchlist', JSON.stringify(watchlist));
  } else {
    console.log(`⚠️ Movie ${tmdbId} already in watchlist`);
  }
};

/**
 * ➖ Удалить фильм из списка "хочу посмотреть"
 * 
 * 🔄 Как работает:
 * 1. Читает текущий watchlist
 * 2. Фильтрует массив, исключая указанный ID
 * 3. Сохраняет обновленный массив
 * 
 * @param tmdbId - ID фильма для удаления
 */
export const removeFromWatchlist = (tmdbId: string | number): void => {
  const watchlist = JSON.parse(localStorage.getItem('rewatch-watchlist') || '[]');
  const updatedWatchlist = watchlist.filter((id: string) => id !== tmdbId.toString());
  localStorage.setItem('rewatch-watchlist', JSON.stringify(updatedWatchlist));
};

/**
 * 📅 Извлечь год из даты выхода фильма
 * 
 * 🔄 Как работает:
 * 1. Получает строку даты в формате "2023-05-12"
 * 2. Создает объект Date и извлекает год
 * 3. Возвращает год как строку или "Unknown year"
 * 
 * 💡 Используется для:
 * - Отображения года в карточках фильмов
 * - Краткой информации в списках
 * 
 * @param releaseDate - Дата в формате "YYYY-MM-DD" или null
 * @returns Год как строка или "Unknown year"
 */
export const getMovieYear = (releaseDate: string | null): string => {
  if (!releaseDate) {
    return 'Unknown year';
  }
  
  try {
    const year = new Date(releaseDate).getFullYear().toString();
    return year;
  } catch (error) {
    console.warn(`⚠️ Invalid date format: ${releaseDate}`);
    return 'Unknown year';
  }
};

/**
 * 🖼️ Сформировать полный URL для постера фильма из TMDB
 * 
 * 🔄 Как работает:
 * 1. Получает путь к постеру от TMDB (например: "/abc123.jpg")
 * 2. Добавляет базовый URL TMDB и размер изображения
 * 3. Возвращает полный URL для <img src="">
 * 
 * 📐 Доступные размеры:
 * - w92: маленькие превью (92px ширина)
 * - w154: средние превью (154px)
 * - w185: карточки в списках (185px)
 * - w342: детальные страницы (342px)
 * - w500: большие изображения (500px)
 * - w780: очень большие (780px)
 * - original: оригинальный размер
 * 
 * 💡 Выбор размера влияет на:
 * - Скорость загрузки (меньше размер = быстрее)
 * - Качество изображения
 * - Трафик пользователя
 * 
 * @param posterPath - Путь к постеру от TMDB API (например: "/abc123.jpg")
 * @param size - Размер изображения (по умолчанию w500)
 * @returns Полный URL изображения
 */
export const getTMDBPosterUrl = (posterPath: string, size: string = 'w500'): string => {
  const fullUrl = `https://image.tmdb.org/t/p/${size}${posterPath}`;
  return fullUrl;
};

/**
 * 📜 Плавно прокрутить страницу к элементу
 * 
 * 🔄 Как работает:
 * 1. Ждет указанную задержку (по умолчанию 100ms)
 * 2. Находит элемент по ID
 * 3. Плавно прокручивает к нему страницу
 * 
 * ⏱️ Зачем задержка?
 * - Элемент может еще рендериться
 * - DOM может быть не готов
 * - Анимации могут мешать
 * 
 * 💡 Используется когда:
 * - Пользователь выбрал фильм из поиска → прокрутка к карточке
 * - Переход к форме после выбора
 * - Навигация по длинным страницам
 * 
 * @param elementId - ID элемента для прокрутки (без #)
 * @param delay - Задержка перед прокруткой в миллисекундах
 */
export const scrollToElement = (elementId: string, delay: number = 100): void => {
  
  setTimeout(() => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',    // Плавная анимация
        block: 'nearest'       // Прокрутить минимально необходимое расстояние
      });
    } else {
      console.warn(`⚠️ Element with ID "${elementId}" not found`);
    }
  }, delay);
};

/**
 * 🎭 Конвертировать ID жанров TMDB в названия
 * 
 * 🔄 Как работает:
 * 1. Получает массив genre_ids от TMDB (например: [28, 12, 16])
 * 2. Использует маппинг ID → название жанра
 * 3. Возвращает массив строк с названиями жанров
 * 
 * 📋 Маппинг основан на официальных ID жанров TMDB:
 * - 28: Action
 * - 12: Adventure
 * - 16: Animation
 * - 35: Comedy
 * - 80: Crime
 * - 99: Documentary
 * - 18: Drama
 * - 10751: Family
 * - 14: Fantasy
 * - 36: History
 * - 27: Horror
 * - 10402: Music
 * - 9648: Mystery
 * - 10749: Romance
 * - 878: Science Fiction
 * - 10770: TV Movie
 * - 53: Thriller
 * - 10752: War
 * - 37: Western
 * 
 * 💡 Используется для:
 * - Отображения жанров в рекомендациях
 * - Фильтрации по жанрам
 * - Статистики по жанрам
 * 
 * @param genreIds - Массив ID жанров от TMDB (например: [28, 12])
 * @param maxGenres - Максимальное количество жанров для возврата (по умолчанию 2)
 * @returns Массив названий жанров (например: ["Action", "Adventure"])
 */
export const getGenresFromIds = (genreIds: number[] | undefined, maxGenres: number = 2): string[] => {
  if (!genreIds || !Array.isArray(genreIds) || genreIds.length === 0) {
    return [];
  }

  // Маппинг ID жанров TMDB на названия (английские, можно локализовать позже)
  const genreMap: { [key: number]: string } = {
    28: 'Action',
    12: 'Adventure',
    16: 'Animation',
    35: 'Comedy',
    80: 'Crime',
    99: 'Documentary',
    18: 'Drama',
    10751: 'Family',
    14: 'Fantasy',
    36: 'History',
    27: 'Horror',
    10402: 'Music',
    9648: 'Mystery',
    10749: 'Romance',
    878: 'Science Fiction',
    10770: 'TV Movie',
    53: 'Thriller',
    10752: 'War',
    37: 'Western'
  };

  const genres = genreIds
    .map(id => genreMap[id])
    .filter(genre => genre !== undefined) // Убираем неизвестные жанры
    .slice(0, maxGenres); // Берем только первые maxGenres

  return genres;
};
